import { useCallback, useRef, useState } from 'react';
import { parseSearchQuery } from '../../services/nlSearchService';
import { NL_SEARCH_MIN_CHARS } from '../data/nlSearchConstants';
import { getNlSearchQuotaMessage, isNlSearchQuotaExceeded } from '../utils/nlSearchQuota';
import {
  buildLotViewQuotaExceededMessage,
  isLotViewQuotaForbiddenResult,
  parseLotViewQuotaFromHttpResult,
} from '../utils/lotViewQuota';

/**
 * Una request por confirmación (Enter). No debounce: el modelo no se llama al tipear.
 * 429 `NL_SEARCH_QUOTA_EXCEEDED` (cupo diario NL): sin reintento, `errorCode` lo
 * marca para que la UI ofrezca Filtros. No toca filtros ni cámara.
 * @param {{ enabled?: boolean, context?: string|null, onQuotaInfo?: function }} [options]
 */
export function useNaturalLanguageSearch({ enabled = true, context = null, onQuotaInfo } = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [result, setResult] = useState(null);
  const abortRef = useRef(null);
  const onQuotaInfoRef = useRef(onQuotaInfo);
  onQuotaInfoRef.current = onQuotaInfo;

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
    setErrorCode(null);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
    setError(null);
    setErrorCode(null);
    setResult(null);
  }, []);

  const parse = useCallback(async (query, extra = {}) => {
    if (!enabled) return { data: null, error: null };

    const trimmed = String(query || '').trim();
    if (trimmed.length < NL_SEARCH_MIN_CHARS) return { data: null, error: null };

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setErrorCode(null);
    setResult(null);

    try {
      const envelope = await parseSearchQuery(trimmed, {
        signal: controller.signal,
        context,
        listingCurrency: extra.listingCurrency,
        listingPriceBands: extra.listingPriceBands,
        viewProvince: extra.viewProvince,
      });
      if (controller.signal.aborted || abortRef.current !== controller) {
        return { data: null, error: null };
      }

      if (isNlSearchQuotaExceeded(envelope)) {
        const message = getNlSearchQuotaMessage(envelope);
        setError(message);
        setErrorCode(envelope.code);
        setResult(null);
        return { data: null, error: message, code: envelope.code };
      }

      if (isLotViewQuotaForbiddenResult(envelope)) {
        const quota = parseLotViewQuotaFromHttpResult(envelope);
        if (quota) onQuotaInfoRef.current?.(quota);
        const message = buildLotViewQuotaExceededMessage(quota?.resetsAt);
        setError(message);
        setResult(null);
        return { data: null, error: message };
      }

      if (!envelope?._httpOk || !envelope.data) {
        const status = envelope?._httpStatus;
        const message = status === 401
          ? 'Iniciá sesión para buscar por lenguaje natural.'
          : status === 400
            ? 'La búsqueda es demasiado corta.'
            : status === 503
              ? 'El servicio de interpretación no está disponible. Intentá de nuevo.'
              : 'No se pudo interpretar la búsqueda. Intentá de nuevo.';
        setError(message);
        setResult(null);
        return { data: null, error: message };
      }

      setResult(envelope.data);
      return { data: envelope.data, error: null };
    } catch (err) {
      if (err?.name === 'AbortError') return { data: null, error: null };
      console.error('Error parsing natural-language search:', err);
      const message = err?.name === 'TimeoutError'
        ? 'La interpretación tardó demasiado. Intentá de nuevo.'
        : 'No se pudo interpretar la búsqueda. Intentá de nuevo.';
      setError(message);
      setResult(null);
      return { data: null, error: message };
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [enabled, context]);

  return {
    loading,
    error,
    errorCode,
    result,
    parse,
    cancel,
    clearResult,
    minChars: NL_SEARCH_MIN_CHARS,
  };
}
